/// <amd-module name="Repos/Tfvc/Views/ShelvesetsHub/Components/ShelvesetsHub" />
import * as React from "react";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { getReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { getTfvcRoutingService, ITfvcRoutingService } from "Repos/Tfvc/Common/TfvcRoutingService";
import * as Resources from "Repos/Tfvc/Views/ShelvesetsHub/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, useComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { Card } from "VSSUI/Card";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { IdentityPickerDropdown, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { TextField, TextFieldFocusTreatmentBehavior, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ZeroData } from "VSSUI/ZeroData";
import { getTfvcClient } from "../RestClient/Tfvc";
import { getShelvesetsListDataProviderService, IShelveset } from "../ShelvesetsListDataProviderService";
import { ShelvesetsTable } from "./ShelvesetsTable";

class ShelvesetsHub extends VssComponent {
    private owner: ObservableValue<IIdentity | undefined>;
    private pickerProvider: IPeoplePickerProvider;
    private routingService: ITfvcRoutingService;
    private shelvesets: ObservableValue<(IShelveset | undefined)[]>;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.routingService = getTfvcRoutingService(context.pageContext);
        this.pickerProvider = new PeoplePickerProvider(
            context.pageContext,
            {
                options: {
                    MinResults: 5,
                    MaxResults: 20
                }
            },
            this.filterIdentities
        );

        const initialData = getShelvesetsListDataProviderService(context.pageContext).getInitialShelvesets();
        this.shelvesets = new ObservableValue(initialData ? initialData.shelvesets : []);

        const currentUser = context.pageContext.getService<IVssPageService>("IVssPageService").getData()!.user;
        this.owner = new ObservableValue(convertToIdentity(currentUser));

        this.processShelvesets(this.shelvesets.value);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <ScreenSizeObserver>
                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                        const isExtraSmallWidth = screenSizeProps.screenSize === ScreenSize.xsmall;

                        return (
                            <Page className="flex-grow custom-scrollbar scroll-auto-hide">
                                <CustomHeader className="repos-commits-hub-header">
                                    <div className="bolt-header-content-area flex-row flex-grow flex-self-stretch">
                                        <HeaderTitleArea>
                                            <HeaderTitleRow>
                                                <HeaderTitle titleSize={TitleSize.Large}>{Resources.ShelvesetsPageHeader}</HeaderTitle>
                                            </HeaderTitleRow>
                                        </HeaderTitleArea>
                                        {!isExtraSmallWidth && (
                                            <>
                                                <div className="flex-row margin-left-8">
                                                    <IdentityPickerDropdown
                                                        className="bolt-text-filterbaritem bolt-textfield-inline"
                                                        editPlaceholder={Resources.FilterByOwner}
                                                        onChange={user => this.loadData(user)}
                                                        onClear={() => (this.owner.value = undefined)}
                                                        pickerProvider={this.pickerProvider}
                                                        placeholder={Resources.FilterByOwner}
                                                        value={this.owner}
                                                    />
                                                </div>
                                                <div className="flex-row margin-left-8" role="search">
                                                    <ShelvesetSearchTextField />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </CustomHeader>
                                <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                                    <ReposMessages className="flex-noshrink" showPullRequestSuggestions={false} />
                                    <Observer shelvesets={this.shelvesets}>
                                        {() => {
                                            if (this.shelvesets.value.length === 0) {
                                                return (
                                                    <Card className="flex-center" contentProps={{ contentPadding: false }}>
                                                        <ZeroData
                                                            imagePath={getAssetLocation(
                                                                this.context.pageContext,
                                                                "ms.vss-code-web/tfvc-shelvesets-hub-content/Content/no-results.png"
                                                            )}
                                                            imageAltText={"Image"}
                                                            primaryText={Resources.NoResultsPrimaryText}
                                                            secondaryText={Resources.NoResultsSecondaryText}
                                                        />
                                                    </Card>
                                                );
                                            }

                                            return (
                                                <Card className="bolt-table-card" contentProps={{ contentPadding: false }}>
                                                    <ShelvesetsTable shelvesets={this.shelvesets.value} showHeader={false} />
                                                </Card>
                                            );
                                        }}
                                    </Observer>
                                </div>
                            </Page>
                        );
                    }}
                </ScreenSizeObserver>
            </Surface>
        );
    }

    private loadData(user: IIdentity | undefined) {
        const currentUser = this.owner.value && this.owner.value.localId;
        const newUser = user && user.localId;

        this.owner.value = user;
        if (this.owner.value && newUser != currentUser) {
            this.shelvesets.value = [undefined];
            getShelvesetsListDataProviderService(this.context.pageContext)
                .fetchShelvesets(this.owner.value)
                .then(results => {
                    if (results) {
                        this.processShelvesets(results.shelvesets);
                        this.shelvesets.value = results.shelvesets;
                    }
                });
        }
    }

    private processShelvesets(shelvesets: Array<IShelveset | undefined>) {
        for (const shelveset of shelvesets) {
            if (shelveset) {
                shelveset.linkProps = {
                    href: this.routingService.getShelvesetDetailsUrl(`${shelveset.shelvesetName};${shelveset.owner}`)
                };
            }
        }
    }

    // Only show identities that have local ids set.
    private filterIdentities = (entityList: IIdentity[]) => entityList && entityList.filter(entity => !!entity.localId);
}

function ShelvesetSearchTextField() {
    const { pageContext } = useComponentContext();
    const searchValue = new ObservableValue("");
    const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        switch (ev.which) {
            case KeyCode.enter:
                doSearch();
                break;

            case KeyCode.escape:
                searchValue.value = "";
                break;

            default:
                return;
        }

        // We only get here if the keypress has been handled.
        ev.preventDefault();
        ev.stopPropagation();
    };

    const doSearch = () => {
        if (!searchValue.value || !searchValue.value.trim()) {
            return;
        }

        let shelvesetName = searchValue.value;
        if (shelvesetName.indexOf(";") < 0) {
            // The endpoint would throw if shelveset name is not in the format "id;user".
            shelvesetName += ";";
        }

        getTfvcClient(pageContext)
            .getShelveset(shelvesetName)
            .then(
                shelveset => {
                    if (shelveset) {
                        const url = getTfvcRoutingService(pageContext).getShelvesetDetailsUrl(`${shelveset.name};${shelveset.owner.uniqueName}`);
                        FastPageSwitch(pageContext, url, true);
                    }
                },
                (error: Error) => {
                    getReposMessagesService(pageContext).showMessage({
                        renderMessage: () => <span>{error.message}</span>,
                        severity: MessageCardSeverity.Warning
                    });
                }
            );
    };

    return (
        <TextField
            ariaLabel={Resources.SearchShelvesetId}
            className="bolt-text-filterbaritem flex-grow"
            containerClassName="flex-grow"
            inputClassName="bolt-text-filterbaritem-input"
            focusTreatment={TextFieldFocusTreatmentBehavior.none}
            onKeyDown={onKeyDown}
            onChange={(event, value) => (searchValue.value = value)}
            placeholder={Resources.SearchShelvesetId}
            style={TextFieldStyle.inline}
            suffixIconProps={{ iconName: "Search", onClick: doSearch }}
            value={searchValue}
            width={TextFieldWidth.tabBar}
        />
    );
}

function convertToIdentity(identityRef: IdentityRef): IIdentity {
    return {
        entityId: "",
        entityType: "",
        originId: identityRef.id,
        localId: identityRef.id,
        originDirectory: "",
        displayName: identityRef.displayName,
        image: identityRef.imageUrl
    };
}

Components.add("ms.vss-code-web.tfvc-shelvesets-hub", ShelvesetsHub);
